import { Button } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import withErrorBoundary from 'common/components/withErrorBoundary';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useNotifications } from 'modules/app/notifications';
import { SfsUkPasswordEducation } from 'modules/cards/components/SfsUkPasswordEducation/SfsUkPasswordEducation';
import { SfsUkPhoneNumberModal } from 'modules/cards/components/SfsUkPhoneNumberModal/SfsUkPhoneNumberModal';
import { getUserFactorsAndCheckIfPhoneActive } from 'modules/physical-cards/apis/getUserFactorsAndCheckIfPhoneActive';
import { SessionStorageKey } from 'src/core/constants/storage';
import { SFSUkScaPasswordModal } from 'src/core/modules/cards/components/SFSUkScaPasswordModal/SFSUkScaPasswordModal';
import type { ApiCard } from 'src/core/modules/physical-cards/card';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { getSessionItem as getItem, removeItem } from 'src/core/utils/storage';
import { logger } from 'src/utils/datadog-log-wrapper';

import { usePlasticCard } from '../../../hooks/usePlasticCard';

type Props = {
  isBlocked: boolean;
  cardStatus: ApiCard['status'];
  isDisabled?: boolean;
};

const SfsUkRevealPaymentPasswordButton = ({
  isBlocked,
  cardStatus,
  isDisabled,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif } = useNotifications();

  useEffect(() => {
    const cardActivated = getItem(SessionStorageKey.CardActivated);
    if (cardActivated) {
      setIsPasswordEducationOpen(true);
      removeItem(SessionStorageKey.CardActivated, sessionStorage);
    }
  }, []);

  const company = useCompany();
  const card = usePlasticCard();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneNumberModalOpen, setIsPhoneNumberModalOpen] = useState(false);
  const [isPasswordEducationOpen, setIsPasswordEducationOpen] = useState(false);

  const isSFSUk = company.banking_provider === 'sfs' && card.currency === 'GBP';
  if (!isSFSUk || !card || isBlocked) {
    return null;
  }

  const isNonActivatedSfsCard =
    card.banking_provider === 'sfs' && cardStatus === 'PRE';

  const isButtonDisabled = isNonActivatedSfsCard || isLoading;

  return (
    <>
      <Button
        variant="secondaryNeutral"
        className="mt-24"
        text={t('cards.actions.seePaymentPassword')}
        onClick={async () => {
          try {
            setIsLoading(true);
            const isMfaActive = await getUserFactorsAndCheckIfPhoneActive();
            if (!isMfaActive) {
              setIsPhoneNumberModalOpen(true);
              return;
            }
            track(AnalyticEventName.CARD_SEE_OTP_REVEAL);
            setIsOpen(true);
          } catch {
            logger.warn('Failed to generate payment password', {
              team: 'cards-ux',
              scope: 'card::sfs-uk-payment-password-reveal',
            });
            dangerNotif(t('misc.somethingWrong'));
          } finally {
            setIsLoading(false);
          }
        }}
        isDisabled={isButtonDisabled || isDisabled}
        isLoading={isLoading}
      />
      {isPhoneNumberModalOpen && (
        <SfsUkPhoneNumberModal
          onClose={() => setIsPhoneNumberModalOpen(false)}
          isOpen={isPhoneNumberModalOpen}
        />
      )}
      {isPasswordEducationOpen && (
        <SfsUkPasswordEducation
          onClose={() => {
            setIsPasswordEducationOpen(false);
          }}
          onShowPasswordModal={() => {
            setIsPasswordEducationOpen(false);
            setIsOpen(true);
          }}
        />
      )}
      {isOpen && (
        <SFSUkScaPasswordModal
          isOpen
          onClose={() => {
            track(AnalyticEventName.CARD_CLOSE_OTP_REVEAL, {
              trigger: 'user',
            });
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
};

const SfsUkRevealPaymentPasswordButtonWithErrorBoundary = withErrorBoundary({
  team: 'cards-ux',
  scope: 'card::sfs-uk-payment-password-reveal',
})(SfsUkRevealPaymentPasswordButton);

export { SfsUkRevealPaymentPasswordButtonWithErrorBoundary as SfsUkRevealPaymentPasswordButton };
